import { parseShiftsCacheKey } from "@/caregiver-app/utils";
import { useOfflineVerbalConfirmationCache } from "@/caregiver-app/hooks/useOfflineVerbalConfirmationCache";
import { base64ToBlob } from "@/common/utils/blob-serialization";
import { isNetworkError } from "@/common/utils/is-network-error";
import { trpc, useTRPCClient } from "@/common/utils/trpc";
import { useLogger } from "@/logging/client/LoggerContext";
import type { RouterInputs } from "@/setup/backend/trpc/trpcRouterTypes";
import { useInvalidateCaregiverAppShiftsQuery } from "@/setup/frontend/api/apiHooks";
import { useCacheStorage } from "@/setup/frontend/storage/CacheStorageContext";
import { TRPCClientError } from "@trpc/client";
import {
	keepPreviousData,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

export function useListMyCareSheets() {
	return trpc.careSheets.listMyCareSheets.useQuery();
}

export function useGetCareSheetCheckOutDetails(shiftId: number) {
	const query = trpc.careSheets.getCareSheetCheckOutDetails.useQuery(
		{ shift_id: shiftId },
		{
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			retryOnMount: true,
			retry: 3,
			retryDelay: 2000,
		},
	);

	// Handle stale offline cache from prior versions where evv_* fields were not yet defined
	if (
		query.data &&
		!query.data.shift.clients.evv_signature_enabled &&
		!query.data.shift.clients.evv_voice_recording_enabled &&
		!query.data.shift.clients.evv_unable_to_verify_reason_enabled
	) {
		if (query.data.shift.clients.unable_to_sign) {
			// preserve behavior by translating unable_to_sign to equivalent evv_ field state
			query.data.shift.clients.evv_signature_enabled = true;
			query.data.shift.clients.evv_unable_to_verify_reason_enabled = true;
		} else {
			// every client defaults to signature enabled
			query.data.shift.clients.evv_signature_enabled = true;
		}
	}

	return query;
}

export function useGetCareSheetPDFDetails(careSheetId: number) {
	return trpc.careSheets.getCareSheetPDFDetails.useQuery(
		{ care_sheet_id: careSheetId },
		{
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			placeholderData: keepPreviousData,
		},
	);
}

export function useGetCareSheetPDFFile(careSheetId: number) {
	return trpc.careSheets.getCareSheetPDFFile.useQuery(
		{ care_sheet_id: careSheetId },
		{
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		},
	);
}

export function useSaveCareSheetReview() {
	const utils = trpc.useUtils();
	return trpc.careSheets.saveCareSheetReview.useMutation({
		onSuccess: (_, { care_sheet_id }) => {
			utils.careSheets.listMyCareSheets.invalidate();
			utils.careSheets.getCareSheetPDFDetails.invalidate({ care_sheet_id });
		},
	});
}

export function useCheckInOutOfflineCache() {
	const cache = useCacheStorage();
	const queryClient = useQueryClient();
	const utils = trpc.useUtils();

	const getCheckIn = (shiftId: number) => {
		return cache
			.getItem(`offline-check-in-${shiftId}`)
			.then((data) => (data ? JSON.parse(data) : null)) as Promise<
			RouterInputs["careSheets"]["saveCareSheetCheckInOut"] | null
		>;
	};

	const getCheckOut = (shiftId: number) => {
		return cache
			.getItem(`offline-check-out-${shiftId}`)
			.then((data) => (data ? JSON.parse(data) : null)) as Promise<
			RouterInputs["careSheets"]["saveCareSheetCheckInOut"] | null
		>;
	};

	// Helper function to update shifts query with offline data
	const updateShiftsQueryWithOfflineData = (
		shiftId: number,
		checkInTime?: string,
		checkOutTime?: string,
	) => {
		// Update all infinity queries for shifts
		queryClient.setQueriesData(
			{ queryKey: ["caregiverAppGetShifts"], type: "all" },
			(oldData: any) => {
				if (!oldData?.pages) return oldData;

				return {
					...oldData,
					pages: oldData.pages.map((page: any[]) =>
						page.map((shift) => {
							if (shift.id === shiftId) {
								return {
									...shift,
									checkIn: checkInTime || shift.checkIn,
									offlineCheckIn: !!checkInTime,
									checkOut: checkOutTime || shift.checkOut,
									offlineCheckOut: !!checkOutTime,
								};
							}
							return shift;
						}),
					),
				};
			},
		);
	};

	// Helper function to update offline cache with offline data
	const updateOfflineCaregiverAppShiftsCache = async (
		shiftId: number,
		checkInTime?: string,
		checkOutTime?: string,
	) => {
		try {
			// Get all cached shifts keys
			const keys = await cache.keys();
			const shiftsKeys = keys.filter((key) =>
				key.startsWith("caregiverAppGetShifts-"),
			);

			// Update each cached shifts page
			for (const key of shiftsKeys) {
				try {
					const cachedData = await cache.getItem(key);
					if (cachedData) {
						const data = JSON.parse(cachedData);

						// Check if this page contains the shift we need to update
						let hasShift = false;
						const updatedData = data.map((shift: any) => {
							if (shift.id === shiftId) {
								hasShift = true;
								return {
									...shift,
									checkIn: checkInTime || shift.checkIn,
									checkOut: checkOutTime || shift.checkOut,
									// reset offline flags because we just deleted the offline edits
									offlineCheckIn: false,
									offlineCheckOut: false,
								};
							}
							return shift;
						});

						// Only update the cache if this page contained the shift
						if (hasShift) {
							await cache.setItem(key, JSON.stringify(updatedData));
						}
					}
				} catch (error) {
					// Continue with other keys if one fails
					console.warn(
						`Failed to update cached shifts data for key ${key}:`,
						error,
					);
				}
			}
		} catch (error) {
			console.warn(
				"Failed to update offline caregiverAppGetShifts cache:",
				error,
			);
		}
	};

	// Helper function to update checkout details query with offline data
	const updateCheckoutDetailsQueryWithOfflineData = (
		shiftId: number,
		offlineData?: RouterInputs["careSheets"]["saveCareSheetCheckInOut"],
	) => {
		if (!offlineData) return;

		utils.careSheets.getCareSheetCheckOutDetails.setData(
			{ shift_id: shiftId },
			(oldData: any) => {
				if (!oldData) return oldData;

				// Create updated timesheets object
				const updatedTimesheets = oldData.shift.timesheets
					? {
							...oldData.shift.timesheets,
							check_in:
								offlineData.checkInOut.checkIn ||
								oldData.shift.timesheets.check_in,
							check_out:
								offlineData.checkInOut.checkOut ||
								oldData.shift.timesheets.check_out,
						}
					: {
							check_in: offlineData.checkInOut.checkIn,
							check_out: offlineData.checkInOut.checkOut,
							check_in_type: 2, // manual entry type
							check_out_type: 2, // manual entry type
						};

				return {
					...oldData,
					shift: {
						...oldData.shift,
						timesheets: updatedTimesheets,
						completed_tasks:
							offlineData.completedTasks || oldData.shift.completed_tasks,
						client_initials:
							offlineData.clientInitials || oldData.shift.client_initials,
						client_signature:
							offlineData.clientSignature || oldData.shift.client_signature,
						caregiver_signature:
							offlineData.caregiverSignature ||
							oldData.shift.caregiver_signature,
						caregiver_initials:
							offlineData.caregiverInitials || oldData.shift.caregiver_initials,
						caregiver_comment:
							offlineData.caregiverComment || oldData.shift.caregiver_comment,
						transportation_data:
							offlineData.transportationData ||
							oldData.shift.transportation_data,
						client_unable_to_sign_reason:
							offlineData.clientUnableToSignReason ||
							oldData.shift.client_unable_to_sign_reason,
						client_verbal_confirmation_recording_id:
							offlineData.clientVerbalConfirmationRecordingId ||
							oldData.shift.client_verbal_confirmation_recording_id,
						client_verbal_confirmation_recording_content_type:
							offlineData.clientVerbalConfirmationRecordingContentType ||
							oldData.shift.client_verbal_confirmation_recording_content_type,
					},
				};
			},
		);
	};

	// Helper function to update offline cache checkout details with offline data
	const updateOfflineCheckoutDetailsCache = async (
		shiftId: number,
		offlineData?: RouterInputs["careSheets"]["saveCareSheetCheckInOut"],
	) => {
		if (!offlineData) return;

		try {
			// Get cached checkout details
			const cachedData = await cache.getItem("offline-check-out-details");
			if (!cachedData) return;

			const checkOutDetails = JSON.parse(cachedData);
			if (!Array.isArray(checkOutDetails)) return;

			// Find and update the matching shift
			let hasShift = false;
			const updatedDetails = checkOutDetails.map((item: any) => {
				if (item.shift?.id === shiftId) {
					hasShift = true;

					// Create updated timesheets object
					const updatedTimesheets = item.shift.timesheets
						? {
								...item.shift.timesheets,
								check_in:
									offlineData.checkInOut.checkIn ||
									item.shift.timesheets.check_in,
								check_out:
									offlineData.checkInOut.checkOut ||
									item.shift.timesheets.check_out,
							}
						: {
								check_in: offlineData.checkInOut.checkIn,
								check_out: offlineData.checkInOut.checkOut,
								check_in_type: 2, // manual entry type
								check_out_type: 2, // manual entry type
							};

					return {
						...item,
						shift: {
							...item.shift,
							timesheets: updatedTimesheets,
							completed_tasks:
								offlineData.completedTasks || item.shift.completed_tasks,
							client_initials:
								offlineData.clientInitials || item.shift.client_initials,
							client_signature:
								offlineData.clientSignature || item.shift.client_signature,
							caregiver_signature:
								offlineData.caregiverSignature ||
								item.shift.caregiver_signature,
							caregiver_initials:
								offlineData.caregiverInitials || item.shift.caregiver_initials,
							caregiver_comment:
								offlineData.caregiverComment || item.shift.caregiver_comment,
							transportation_data:
								offlineData.transportationData ||
								item.shift.transportation_data,
							client_unable_to_sign_reason:
								offlineData.clientUnableToSignReason ||
								item.shift.client_unable_to_sign_reason,
							client_verbal_confirmation_recording_id:
								offlineData.clientVerbalConfirmationRecordingId ||
								item.shift.client_verbal_confirmation_recording_id,
							client_verbal_confirmation_recording_content_type:
								offlineData.clientVerbalConfirmationRecordingContentType ||
								item.shift.client_verbal_confirmation_recording_content_type,
						},
					};
				}
				return item;
			});

			// Only update the cache if the shift was found and modified
			if (hasShift) {
				await cache.setItem(
					"offline-check-out-details",
					JSON.stringify(updatedDetails),
				);
			}
		} catch (error) {
			console.warn("Failed to update offline checkout details cache:", error);
		}
	};

	const setCheckIn = (
		shiftId: number,
		data: RouterInputs["careSheets"]["saveCareSheetCheckInOut"],
	) => {
		return cache
			.setItem(
				`offline-check-in-${shiftId}`,
				JSON.stringify({
					...data,
					checkInOut: {
						...data.checkInOut,
						checkInMetadata: data.checkInOut.checkInMetadata
							? {
									...data.checkInOut.checkInMetadata,
									offline: true,
								}
							: undefined,
						checkOutMetadata: data.checkInOut.checkOutMetadata
							? {
									...data.checkInOut.checkOutMetadata,
									offline: true,
								}
							: undefined,
					},
				} satisfies RouterInputs["careSheets"]["saveCareSheetCheckInOut"]),
			)
			.then(() => {
				queryClient.invalidateQueries({
					queryKey: ["offline", "check-in", shiftId],
				});

				// Update query data with offline check-in
				updateShiftsQueryWithOfflineData(
					shiftId,
					data.checkInOut.checkIn,
					undefined,
				);
				updateCheckoutDetailsQueryWithOfflineData(shiftId, data);
			});
	};

	const removeCheckIn = (shiftId: number) => {
		return cache.removeItem(`offline-check-in-${shiftId}`).finally(() => {
			queryClient.invalidateQueries({
				queryKey: ["offline", "check-in", shiftId],
			});
			updateShiftsQueryWithOfflineData(shiftId, undefined, undefined);
			updateCheckoutDetailsQueryWithOfflineData(shiftId, undefined);
		});
	};

	const setCheckOut = (
		shiftId: number,
		data: RouterInputs["careSheets"]["saveCareSheetCheckInOut"],
	) => {
		return cache
			.setItem(
				`offline-check-out-${shiftId}`,
				JSON.stringify({
					...data,
					checkInOut: {
						...data.checkInOut,
						checkInMetadata: data.checkInOut.checkInMetadata
							? {
									...data.checkInOut.checkInMetadata,
									offline: true,
								}
							: undefined,
						checkOutMetadata: data.checkInOut.checkOutMetadata
							? {
									...data.checkInOut.checkOutMetadata,
									offline: true,
								}
							: undefined,
					},
				} satisfies RouterInputs["careSheets"]["saveCareSheetCheckInOut"]),
			)
			.then(() => {
				queryClient.invalidateQueries({
					queryKey: ["offline", "check-out", shiftId],
				});

				// Update query data with offline check-out
				updateShiftsQueryWithOfflineData(
					shiftId,
					data.checkInOut.checkIn,
					data.checkInOut.checkOut,
				);
				updateCheckoutDetailsQueryWithOfflineData(shiftId, data);
			});
	};

	const removeCheckOut = (shiftId: number) => {
		return cache.removeItem(`offline-check-out-${shiftId}`).finally(() => {
			queryClient.invalidateQueries({
				queryKey: ["offline", "check-out", shiftId],
			});

			updateShiftsQueryWithOfflineData(shiftId, undefined, undefined);
			updateCheckoutDetailsQueryWithOfflineData(shiftId, undefined);
		});
	};

	const getCheckInShiftIds = () => {
		return cache
			.keys()
			.then((keys) =>
				keys
					.filter((key) => key.startsWith("offline-check-in-"))
					.map((key) => Number.parseInt(key.split("-").pop()!)),
			);
	};

	const getCheckOutShiftIds = () => {
		return cache
			.keys()
			.then((keys) =>
				keys
					.filter((key) => key.startsWith("offline-check-out-"))
					.map((key) => Number.parseInt(key.split("-").pop()!)),
			);
	};

	return {
		setCheckIn,
		setCheckOut,
		removeCheckIn,
		removeCheckOut,
		getCheckIn,
		getCheckOut,
		getCheckInShiftIds,
		getCheckOutShiftIds,
		updateShiftsQueryWithOfflineData,
		updateOfflineCaregiverAppShiftsCache,
		updateCheckoutDetailsQueryWithOfflineData,
		updateOfflineCheckoutDetailsCache,
	};
}

export function useSyncOfflineCheckInOuts() {
	const utils = trpc.useUtils();
	const client = useTRPCClient();

	const {
		removeCheckIn,
		removeCheckOut,
		getCheckInShiftIds,
		getCheckOutShiftIds,
		getCheckIn,
		getCheckOut,
		updateCheckoutDetailsQueryWithOfflineData,
		updateShiftsQueryWithOfflineData,
		updateOfflineCaregiverAppShiftsCache,
		updateOfflineCheckoutDetailsCache,
	} = useCheckInOutOfflineCache();

	const verbalConfirmationCache = useOfflineVerbalConfirmationCache();
	const invalidateShiftsQuery = useInvalidateCaregiverAppShiftsQuery();
	const logger = useLogger();

	const sync = async () => {
		const checkInShiftIds = await getCheckInShiftIds();
		const checkOutShiftIds = await getCheckOutShiftIds();

		if (checkInShiftIds.length === 0 && checkOutShiftIds.length === 0) {
			return;
		}

		const successfullySyncedShiftIds: number[] = [];

		// Group by shift ID and create a map of operations to perform
		const shiftOperations = new Map<
			number,
			{
				checkIn?: RouterInputs["careSheets"]["saveCareSheetCheckInOut"];
				checkOut?: RouterInputs["careSheets"]["saveCareSheetCheckInOut"];
			}
		>();

		// Process check-ins
		for (const shiftId of checkInShiftIds) {
			const data = await getCheckIn(shiftId);
			if (data && typeof data === "object" && "shift_id" in data) {
				if (!shiftOperations.has(shiftId)) {
					shiftOperations.set(shiftId, {});
				}
				shiftOperations.get(shiftId)!.checkIn = data;
			}
		}

		// Process check-outs
		for (const shiftId of checkOutShiftIds) {
			const data = await getCheckOut(shiftId);
			if (data && typeof data === "object" && "shift_id" in data) {
				if (!shiftOperations.has(shiftId)) {
					shiftOperations.set(shiftId, {});
				}
				shiftOperations.get(shiftId)!.checkOut = data;
			}
		}

		// Process each shift: run check-in before check-out
		for (const [shiftId, operations] of shiftOperations) {
			// Handle check-in
			if (operations.checkIn) {
				try {
					await utils.client.careSheets.saveCareSheetCheckInOut.mutate(
						operations.checkIn,
					);
					logger.info(`Synced check-in for shift ${shiftId}`);
					// Remove check-in from cache after successful sync
					await removeCheckIn(shiftId);
					// update offline data to persist optimistic update until all data is refreshed from server
					await updateOfflineCaregiverAppShiftsCache(
						shiftId,
						operations.checkIn.checkInOut.checkIn,
						undefined,
					);
					await updateOfflineCheckoutDetailsCache(shiftId, operations.checkIn);
					successfullySyncedShiftIds.push(shiftId);
				} catch (error: unknown) {
					logger.error(
						`Error syncing check-in for shift ${shiftId}. isNetworkError: ${isNetworkError(error)} Error: ${error instanceof Error ? error.message : error}`,
					);
					if (!isNetworkError(error)) {
						// Remove from cache if it's not a network error (e.g., validation error)
						await removeCheckIn(shiftId);
					} else {
						updateShiftsQueryWithOfflineData(
							shiftId,
							operations.checkIn.checkInOut.checkIn,
							undefined,
						);
						updateCheckoutDetailsQueryWithOfflineData(
							shiftId,
							operations.checkIn,
						);
					}
				}
			}

			// Handle check-out
			if (operations.checkOut) {
				try {
					// Upload cached verbal confirmation recording before checkout
					if (operations.checkOut.clientVerbalConfirmationRecordingId) {
						logger.info(
							`verbal_confirmation_sync_started shiftId=${shiftId} recordingId=${operations.checkOut.clientVerbalConfirmationRecordingId}`,
						);
						const cachedRecording = await verbalConfirmationCache.get(shiftId);
						if (cachedRecording) {
							logger.info(
								`verbal_confirmation_sync_cache_found shiftId=${shiftId} recordingId=${cachedRecording.recordingId} sizeBytes=${cachedRecording.sizeBytes} contentType=${cachedRecording.contentType} storedAt=${cachedRecording.storedAt}`,
							);
							try {
								const { uploadUrl } =
									await client.shifts.getClientVerbalConfirmationUploadUrl.mutate(
										{
											shift_id: shiftId,
											recording_id: cachedRecording.recordingId,
											file_size_bytes: cachedRecording.sizeBytes,
											content_type: cachedRecording.contentType,
										},
									);
								const blob = base64ToBlob(
									cachedRecording.base64,
									cachedRecording.contentType,
								);
								const response = await fetch(uploadUrl, {
									method: "PUT",
									body: blob,
									headers: { "Content-Type": cachedRecording.contentType },
								});
								if (!response.ok) {
									throw new Error(
										`S3 upload failed with status ${response.status}`,
									);
								}
								logger.info(
									`verbal_confirmation_sync_upload_succeeded shiftId=${shiftId} recordingId=${cachedRecording.recordingId}`,
								);
							} catch (error) {
								if (
									error instanceof TRPCClientError &&
									error.data?.code === "CONFLICT"
								) {
									logger.info(
										`Verbal confirmation recording already uploaded for shift ${shiftId}`,
									);
								} else {
									throw error;
								}
							}
							await verbalConfirmationCache.remove(shiftId);
							logger.info(
								`verbal_confirmation_cache_removed shiftId=${shiftId} recordingId=${cachedRecording.recordingId} reason=synced`,
							);
						} else {
							logger.error(
								`verbal_confirmation_sync_cache_missing shiftId=${shiftId} recordingId=${operations.checkOut.clientVerbalConfirmationRecordingId}`,
							);
						}
					}

					await utils.client.careSheets.saveCareSheetCheckInOut.mutate(
						operations.checkOut,
					);
					logger.info(`Synced check-out for shift ${shiftId}`);
					// Remove check-out from cache after successful sync
					await removeCheckOut(shiftId);
					// update offline data to persist optimistic update until all data is refreshed from server
					await updateOfflineCaregiverAppShiftsCache(
						shiftId,
						undefined,
						operations.checkOut.checkInOut.checkOut,
					);
					await updateOfflineCheckoutDetailsCache(shiftId, operations.checkOut);
					successfullySyncedShiftIds.push(shiftId);
				} catch (error: unknown) {
					logger.error(
						`Error syncing check-out for shift ${shiftId}. isNetworkError: ${isNetworkError(error)} Error: ${error instanceof Error ? error.message : error}`,
					);
					if (!isNetworkError(error)) {
						// Remove from cache if it's not a network error (e.g., validation error)
						await removeCheckOut(shiftId);
					} else {
						updateShiftsQueryWithOfflineData(
							shiftId,
							undefined,
							operations.checkOut.checkInOut.checkOut,
						);
						updateCheckoutDetailsQueryWithOfflineData(
							shiftId,
							operations.checkOut,
						);
					}
				}
			}
		}

		if (successfullySyncedShiftIds.length > 0) {
			utils.careSheets.listMyCareSheets.invalidate();
			for (const shiftId of successfullySyncedShiftIds) {
				utils.careSheets.getCareSheetCheckOutDetails.invalidate({
					shift_id: shiftId,
				});
			}
			invalidateShiftsQuery();
		}
	};

	return { sync };
}

export function useSeedCheckOutDetailsCache() {
	const cache = useCacheStorage();
	const utils = trpc.useUtils();
	const { updateCheckoutDetailsQueryWithOfflineData, getCheckIn, getCheckOut } =
		useCheckInOutOfflineCache();

	const seedCache = async () => {
		try {
			// First, load any existing cached check out details
			const cachedData = await cache.getItem("offline-check-out-details");
			if (cachedData) {
				const checkOutDetails = JSON.parse(cachedData);

				// Set each cached item in React Query cache
				for (const item of checkOutDetails ?? []) {
					// Only set if not already present to avoid overwriting fresh data
					if (
						!utils.careSheets.getCareSheetCheckOutDetails.getData({
							shift_id: item.shift.id,
						})
					) {
						utils.careSheets.getCareSheetCheckOutDetails.setData(
							{ shift_id: item.shift.id },
							item,
						);
					}
				}

				// Now apply any offline check-in/check-out data on top
				for (const item of checkOutDetails ?? []) {
					const [checkInData, checkOutData] = await Promise.all([
						getCheckIn(item.shift.id),
						getCheckOut(item.shift.id),
					]);

					const offlineData = checkOutData || checkInData;
					if (offlineData) {
						updateCheckoutDetailsQueryWithOfflineData(
							item.shift.id,
							offlineData,
						);
					}
				}
			}
		} catch (error) {
			// Silently fail if cache operations fail
			console.warn("Failed to seed check out details cache:", error);
		}
	};

	return { seedCache };
}

export function usePrefetchCheckOutDetails() {
	const utils = trpc.useUtils();

	const cache = useCacheStorage();

	const { updateCheckoutDetailsQueryWithOfflineData, getCheckIn, getCheckOut } =
		useCheckInOutOfflineCache();

	const logger = useLogger();

	const prefetch = async () => {
		try {
			const prefetchCheckOutDetails =
				await utils.client.careSheets.prefetchCareSheetCheckOutDetailsForOffline.query(
					{},
				);
			cache.setItem(
				"offline-check-out-details",
				JSON.stringify(prefetchCheckOutDetails),
			);
			for (const item of prefetchCheckOutDetails ?? []) {
				utils.careSheets.getCareSheetCheckOutDetails.setData(
					{ shift_id: item.shift.id },
					item,
				);
				const checkIn = await getCheckIn(item.shift.id);
				const checkOut = await getCheckOut(item.shift.id);
				if (checkIn || checkOut) {
					updateCheckoutDetailsQueryWithOfflineData(
						item.shift.id,
						checkOut! || checkIn!,
					);
				}
			}
		} catch (e) {
			logger.error(
				`Error prefetching check-out details. Error: ${e instanceof Error ? e.message : e}`,
			);
			if (isNetworkError(e)) {
				cache.getItem("offline-check-out-details").then(async (data) => {
					if (data) {
						for (const item of JSON.parse(data) ?? []) {
							if (
								utils.careSheets.getCareSheetCheckOutDetails.getData({
									shift_id: item.shift.id,
								})
							) {
								continue;
							}
							utils.careSheets.getCareSheetCheckOutDetails.setData(
								{ shift_id: item.shift.id },
								item,
							);
							const checkIn = await getCheckIn(item.shift.id);
							const checkOut = await getCheckOut(item.shift.id);
							if (checkIn || checkOut) {
								updateCheckoutDetailsQueryWithOfflineData(
									item.shift.id,
									checkOut! || checkIn!,
								);
							}
						}
					}
				});
			}
		}
	};

	return { prefetch };
}

export function useSeedCaregiverAppShiftsCache() {
	const cache = useCacheStorage();
	const queryClient = useQueryClient();
	const {
		getCheckIn,
		getCheckOut,
		getCheckInShiftIds,
		getCheckOutShiftIds,
		updateShiftsQueryWithOfflineData,
	} = useCheckInOutOfflineCache();

	const logger = useLogger();

	const seedCache = async () => {
		try {
			// Get all cached shifts keys
			const keys = await cache.keys();
			const shiftsKeys = keys.filter((key) =>
				key.startsWith("caregiverAppGetShifts-"),
			);

			// Load each cached shifts page into React Query cache
			for (const key of shiftsKeys) {
				try {
					const cachedData = await cache.getItem(key);
					if (cachedData) {
						const data = JSON.parse(cachedData);

						const parsed = parseShiftsCacheKey(key);

						if (!parsed) {
							console.warn(`Invalid cache key format: ${key}`);
							continue;
						}

						const { after: afterParam, before: beforeParam } = parsed;

						// Create the query key that matches the React Query cache structure
						const queryKey = ["caregiverAppGetShifts", afterParam, undefined]; // status is undefined for scheduled

						// Set the data in React Query cache as infinite query data
						queryClient.setQueryData(queryKey, {
							pages: [data],
							pageParams: [{ after: afterParam, before: beforeParam }],
						});
					}
				} catch (error) {
					logger.error(
						`Failed to load cached shifts data for key ${key}. Error: ${error instanceof Error ? error.message : error}`,
					);
				}
			}

			// Now apply offline check-in/check-out data to the seeded cache
			const [checkInShiftIds, checkOutShiftIds] = await Promise.all([
				getCheckInShiftIds(),
				getCheckOutShiftIds(),
			]);

			// Combine and deduplicate shift IDs
			const allOfflineShiftIds = [
				...new Set([...checkInShiftIds, ...checkOutShiftIds]),
			];

			// Apply offline data for each shift
			for (const shiftId of allOfflineShiftIds) {
				const [checkInData, checkOutData] = await Promise.all([
					getCheckIn(shiftId),
					getCheckOut(shiftId),
				]);

				const checkInTime =
					checkOutData?.checkInOut?.checkIn || checkInData?.checkInOut?.checkIn;
				const checkOutTime = checkOutData?.checkInOut?.checkOut;

				if (checkInTime || checkOutTime) {
					updateShiftsQueryWithOfflineData(shiftId, checkInTime, checkOutTime);
				}
			}
		} catch (error) {
			logger.error(
				`Failed to seed caregiverAppGetShifts cache. Error: ${error instanceof Error ? error.message : error}`,
			);
		}
	};

	return { seedCache };
}

export const useClearOldCaregiverAppShiftsCache = () => {
	const cache = useCacheStorage();
	const logger = useLogger();
	const clearOldCache = async () => {
		try {
			const keys = await cache.keys();
			const shiftsKeys = keys.filter((key) =>
				key.startsWith("caregiverAppGetShifts-"),
			);

			const maxCacheSize = 5;

			// Keep only the most recent maxCacheSize cache entries to avoid unlimited cache growth
			if (shiftsKeys.length > maxCacheSize) {
				// Parse timestamps from keys and sort from oldest to newest
				const keysWithTimestamps = shiftsKeys
					.map((key) => {
						const parsed = parseShiftsCacheKey(key);

						if (!parsed) {
							console.warn(`Invalid cache key format: ${key}`);
							return null;
						}

						const afterParam = parsed.after ?? null;
						const beforeParam = parsed.before ?? null;

						// Use the 'after' timestamp as primary sort key, fall back to 'before', then to 0
						const primaryTimestamp = afterParam
							? new Date(afterParam).getTime()
							: beforeParam
								? new Date(beforeParam).getTime()
								: 0;

						return {
							key,
							timestamp: primaryTimestamp,
							after: afterParam,
							before: beforeParam,
						};
					})
					.filter((item): item is NonNullable<typeof item> => item !== null);

				// Sort by timestamp (oldest first)
				keysWithTimestamps.sort((a, b) => a.timestamp - b.timestamp);

				// Remove the oldest entries, keeping the most recent maxCacheSize
				const keysToRemove = keysWithTimestamps
					.slice(0, keysWithTimestamps.length - maxCacheSize)
					.map((item) => item.key);

				await Promise.all(keysToRemove.map((key) => cache.removeItem(key)));
			}
		} catch (error) {
			// Silently fail if cache operations fail
			logger.error(
				`Failed to clear old caregiverAppGetShifts cache. Error: ${error instanceof Error ? error.message : error}`,
			);
		}
	};

	return { clearOldCache };
};
